import {
  Attachment,
  OutLookSchedule,
  RecordFilter,
  ScheduleEvent,
} from '@/types/api';
import {
  DataContainer,
  LoadingContainer,
  StyledNoData,
  StyledTargetTime,
  StyledTime,
  StyledTitle,
  TimeContainer,
} from './styles';
import { useFormContext } from 'react-hook-form';
import {
  useGetOutlookAttachments,
  useGetOutlookDetail,
} from '@/hooks/apiHooks/msAzure';
import { useEffect, useState } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import 'dayjs/locale/ko';
import { usePassportSearchOrganization, useUploadFile } from '@/hooks/apiHooks';
import { base64ToFile } from '@/components/Record/util/function';
import CircularProgress from '@mui/material/CircularProgress';
import { formatAs } from '@/utils/format';
import usePlatform from '@/components/Record/hooks/usePlatform';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.locale('ko');

const EVENT_TIMEZONE = 'Asia/Seoul';
const EVENT_FORMAT = 'YYYY-MM-DD (ddd)';

export type MSScheduleFormValue = {
  meetingDate: Dayjs;
  subject: string;
  external: string[];
  inner: string[];
  attachments: Attachment[];
  meetingMsId: string;
  meetingMsSubject: string;
  meetingMsLink: string;
};

type P = {
  data: OutLookSchedule;
  handleClose?: () => void;
};

const parseDate = (str: string) => {
  const [year, month, day] = str.split('-').map(Number);
  return new Date(year, month - 1, day);
};

const formatDate = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const weekday = date.toLocaleDateString('ko-KR', {
    weekday: 'short',
    timeZone: EVENT_TIMEZONE,
  });
  return `${year}-${month}-${day} (${weekday})`;
};

const groupByDateWithRange = (
  events: ScheduleEvent[],
  displayRange: string,
): Record<string, ScheduleEvent[]> => {
  const [startStr, endStr] = displayRange.split(' ~ ');
  const startDate = parseDate(startStr);
  const endDate = parseDate(endStr);

  const dateMap: Record<string, ScheduleEvent[]> = {};

  // 날짜 범위만큼 루프 돌리기
  for (let d = new Date(startDate); d <= endDate; d.setDate(d.getDate() + 1)) {
    const formatted = formatDate(new Date(d));
    dateMap[formatted] = []; // 초기화
  }

  // 이벤트를 해당 날짜에 넣기
  events.forEach((event) => {
    const eventDate = dayjs.utc(event.start.dateTime).tz(EVENT_TIMEZONE);
    const formatted = eventDate.format(EVENT_FORMAT);

    if (dateMap[formatted]) {
      dateMap[formatted].push(event);
    }
  });

  return dateMap;
};

const ScheduleList = ({ data, handleClose }: P) => {
  const { isMobilePath } = usePlatform();
  const displayRange = data.displayRange;
  const events = data.events;
  const grouped = groupByDateWithRange(events, displayRange.formatted);
  const { setValue } = useFormContext<RecordFilter>();
  const [loading, setLoading] = useState(false);

  const [innerAttendees, setInnerAttendees] = useState<string[]>([]);
  const today = dayjs.utc().tz(EVENT_TIMEZONE).format(EVENT_FORMAT);

  const { watch } = useFormContext<RecordFilter>();
  const selectedMsId = watch('meetingMsId');

  const { data: passportSearch } = usePassportSearchOrganization(
    {
      emails: innerAttendees,
      listSize: 1000,
    },
    0,
  );

  useEffect(() => {
    if (passportSearch) {
      setValue(
        'internal',
        passportSearch?.data.map((item) => ({
          passportId: item.passportId,
          name: item.detail.name,
          departmentName: item.detail.departmentName,
          positionName: item.detail.positionName,
          divisionName: item.detail.divisionName,
          profileImageUrl: item.detail.profileImageUrl,
        })),
      );
      handleClose && handleClose();
    }
  }, [passportSearch]);

  // 일정 삽입 시 제목(subject)은 자동 기입하지 않는다. (PORTAL-12831)
  // 제목은 항상 사용자가 직접 입력하고, 그 외 값만 채운다.
  const applyMeetingValues = ({
    meetingDate,
    external,
    meetingMsSubject,
    meetingMsLink,
    meetingMsId,
    inner,
    attachments,
  }: MSScheduleFormValue) => {
    setValue('meetingDate', meetingDate);
    setValue('external', external);
    setValue('meetingMsSubject', meetingMsSubject);
    setValue('meetingMsLink', meetingMsLink);
    setValue('meetingMsId', meetingMsId);
    setValue('attachments', attachments);
    setInnerAttendees(inner);
  };

  const onClick = async (id: string) => {
    setLoading(true);
    const scheduleFormValue = await generateScheduleFormValue(id);

    if (!scheduleFormValue) return setLoading(false);

    applyMeetingValues(scheduleFormValue);
    setLoading(false);
  };

  return (
    <div style={{ position: 'relative' }}>
      {Object.entries(grouped).map(([date, items]) => {
        return (
          items.length > 0 && (
            <DataContainer
              key={date}
              today={today === date}
              mobileMode={isMobilePath}
            >
              <div
                style={{
                  fontSize: '14px',
                  fontWeight: '600',
                  lineHeight: '20px',
                }}
              >
                {date}
              </div>
              <TimeContainer>
                {items.length > 0 ? (
                  items.map((item) => {
                    const time = formatAs(
                      dayjs.utc(item.start.dateTime).tz(EVENT_TIMEZONE),
                      'time',
                    );
                    return (
                      <StyledTargetTime
                        key={item.id}
                        onClick={() => onClick(item.id)}
                        disabled={loading}
                      >
                        <StyledTime selected={selectedMsId === item.id}>
                          {time}
                        </StyledTime>
                        <StyledTitle selected={selectedMsId === item.id}>
                          {item.subject}
                        </StyledTitle>
                      </StyledTargetTime>
                    );
                  })
                ) : (
                  <StyledNoData>예정된 일정이 없습니다.</StyledNoData>
                )}
              </TimeContainer>
            </DataContainer>
          )
        );
      })}
      {loading && (
        <LoadingContainer>
          <CircularProgress />
        </LoadingContainer>
      )}
    </div>
  );
};
export default ScheduleList;

const getUploadedAttachments = async (id: string): Promise<Attachment[]> => {
  const msAttachments = await useGetOutlookAttachments(id);
  return Promise.all(
    msAttachments.value.map(async (attachment) => {
      try {
        const file = base64ToFile(
          attachment.contentBytes,
          attachment.name,
          attachment.contentType,
        );
        const uploadData = await useUploadFile(file, true);
        return {
          name: uploadData.data.fileName,
          uuid: uploadData.data.uuid,
          size: uploadData.data.fileSize,
        };
      } catch (e) {
        console.error(e);
        return null;
      }
    }),
  ).then((list) => list.filter(Boolean) as Attachment[]);
};

export const generateScheduleFormValue = async (
  id: string,
): Promise<MSScheduleFormValue | null> => {
  try {
    const data = await useGetOutlookDetail(id);
    const meetingDate = dayjs.utc(data.start.dateTime).tz(EVENT_TIMEZONE);
    const attendees = data.attendees?.map((a) => a.emailAddress) ?? [];
    const fnfDomain = '@fnfcorp.com';

    // 내부/외부 참석자 분리
    const innerSet = new Set([
      ...attendees
        .filter((a) => a.address.includes(fnfDomain))
        .map((a) => a.address),
      data.organizer.emailAddress.address,
    ]);
    const external = attendees
      .filter((a) => !a.address.includes(fnfDomain))
      .map((a) => a.name);

    // 첨부파일 처리
    const attachments = !data.hasAttachments
      ? []
      : await getUploadedAttachments(data.id);

    // 제목 처리
    const formattedKst = meetingDate
      ? `(${formatAs(meetingDate, 'dateTimeMinute')})`
      : '';
    const subject = `${data.subject ?? ''} ${formattedKst}`;

    return {
      meetingDate,
      subject: subject.length > 100 ? subject.slice(0, 97) + '...' : subject,
      external,
      inner: Array.from(innerSet),
      attachments,
      meetingMsSubject: data.subject,
      meetingMsLink: data.webLink,
      meetingMsId: data.id,
    };
  } catch (error) {
    console.error(error);
    return null;
  }
};
